import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Col, Container, Row, Card, CardHeader, CardBody, Input, Label, Button } from "@/Components/ui";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import MyEditor from "../../../Components/editor/MyEditor";
import "quill/dist/quill.snow.css";
import { handleImageFiles } from "../../../utils/handleImage";
import CommonDatePicker from "@/Components/Common/CommonDatePicker";
import moment from "moment";
import { API } from "../../../api/client";
import { focusFirstMissingField, formatRequiredMessage } from "@/utils/formRequired";
import { dialogService } from "@/utils/dialogService";
import { buildTicketProductPayload, findMissingTicketProductFields, type TicketExpirationUnit } from "./ticketProductForm.shared";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
const TicketProductForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isCopyMode = searchParams.get("copy") === "true";
    const [state, setState] = React.useState("노출중");
    const [name, setName] = React.useState("");
    const [bundle, setBundle] = React.useState<string | number>("");
    const [price, setPrice] = React.useState<string | number>("");
    const [membershipPrice, setMembershipPrice] = React.useState<string | number>("");
    const [stock, setStock] = React.useState<string | number>("");
    const [noLimit, setNoLimit] = React.useState(false);
    const [canRefundEach, setCanRefundEach] = React.useState(false);
    const [unit, setUnit] = React.useState<TicketExpirationUnit>("years");
    const [period, setPeriod] = React.useState<string | number>("");
    const [target_date, setTargetDate] = React.useState(moment().add(365, "days").format("YYYY-MM-DD"));
    const [descript, setDescript] = React.useState("");
    const [thumbnail, setThumbnail] = React.useState([]);
    const [detailImg, setDetailImg] = React.useState([]);
    const [usingInfo, setUsingInfo] = React.useState("");
    const [loadedImg, setLoadedImg] = useState(null);
    const [linkedPrecouponCount, setLinkedPrecouponCount] = React.useState(0);
    const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
    const isLinkedPrecouponTicket = linkedPrecouponCount > 0;
    const markDirty = React.useCallback(() => setHasUnsavedChanges(true), []);
    const { confirmDiscardChanges } = useUnsavedChangesGuard({
        isDirty: hasUnsavedChanges,
        confirmNavigation: dialogService.confirm,
    });
    React.useEffect(() => {
        if (id !== "0") {
            API.get(`/ticket/products/${id}/`)
                .then((response) => {
                fillStates(response.data, isCopyMode);
                console.log("data", response.data);
            })
                .catch((error) => console.error(error));
        }
    }, [isCopyMode]);
    const fillStates = (data, copyMode = false) => {
        setName(copyMode ? data["name"] + " (복사)" : data["name"]);
        setPrice(data["price"]);
        setMembershipPrice(data["membership_price"]);
        const nextLinkedPrecouponCount = Number(data["linked_precoupon_count"] || 0);
        setLinkedPrecouponCount(nextLinkedPrecouponCount);
        setBundle(nextLinkedPrecouponCount > 0 ? 1 : data["bundle_count"]);
        setCanRefundEach(Boolean(data["can_refund_each"]));
        setDescript(data["descript"]);
        setUsingInfo(data["using_info"]);
        setState(data["state"]);
        const expriation_period = data["expiration_period"];
        if (expriation_period["target_date"]) {
            setUnit("target_date");
            setTargetDate(expriation_period["target_date"]);
        }
        else if (expriation_period["months"]) {
            setUnit("months");
            setPeriod(Object.values(expriation_period)[0] as string | number);
        }
        else {
            setUnit("years");
            setPeriod(Object.values(expriation_period)[0] as string | number);
        }
        const itemStock = data["stock_count"];
        if (itemStock < 0)
            setNoLimit(true);
        setStock(itemStock < 0 ? 0 : itemStock);
        // Don't copy images in copy mode - user should upload new ones
        if (!copyMode && data["thumbnail"])
            setLoadedImg(data["thumbnail"]);
    };
    const sendData = async () => {
        const data = await checkData();
        if (data === null)
            return;
        API.post(`/ticket/0/`, data)
            .then(async (response) => {
            setHasUnsavedChanges(false);
            await dialogService.handleAlert("생성 완료");
            navigate(`/ticket-products`);
        })
            .catch(async (error) => {
            console.log(error);
            await dialogService.handleAlert("생성 실패", { force: "error" });
        });
    };
    const updateData = async () => {
        const data = await checkData();
        if (data === null)
            return;
        API.put(`/ticket/${id}/`, data)
            .then(async (response) => {
            setHasUnsavedChanges(false);
            await dialogService.handleAlert("수정 완료");
            navigate(`/ticket-products`);
        })
            .catch(async (error) => {
            console.log(error);
            await dialogService.handleAlert("수정 실패", { force: "error" });
        });
    };
    const checkData = async () => {
      const missingRequiredFields = findMissingTicketProductFields({
        name,
        bundle,
        price,
        membershipPrice,
        stock,
        noLimit,
        unit,
        period,
        targetDate: target_date,
        descript,
        usingInfo,
        thumbnail,
        detailImg,
        state,
        canRefundEach,
      });
        if (missingRequiredFields.length > 0) {
            await dialogService.handleAlert(formatRequiredMessage(missingRequiredFields));
            focusFirstMissingField(missingRequiredFields);
            return null;
        }
      return buildTicketProductPayload({
        name,
        bundle,
        price,
        membershipPrice,
        stock,
        noLimit,
        unit,
        period,
        targetDate: target_date,
        descript,
        usingInfo,
        thumbnail,
        detailImg,
        state,
        canRefundEach,
      });
    };
    useEffect(() => {
        console.log("th", thumbnail);
    }, [thumbnail, detailImg]);
    return (<React.Fragment>
      <div className="page-content" onChangeCapture={markDirty} onInputCapture={markDirty}>
        <Container fluid>
          <BreadCrumb title="티켓 등록 및 수정" pageTitle="티켓"/>

          <Row className="g-4 mb-3">
            <div className="col-sm-auto">
              <div>
                <h4 className="card-title mb-0 flex-grow-1">등록 및 수정</h4>
              </div>
            </div>
            <div className="col-sm-3 ms-auto">
              <div className="d-flex justify-content-sm-end gap-2">
                {(id === "0" || isCopyMode) ? (<Button color="success" className="add-btn me-1" onClick={sendData} id="update-btn">
                    생성
                  </Button>) : (<Button color="success" className="add-btn me-1" onClick={updateData} id="create-btn">
                    수정
                  </Button>)}
                <Button color="danger" className="add-btn me-1" onClick={async () => {
            if (!await confirmDiscardChanges())
                return;
            navigate(`/ticket-products`);
        }} id="cancel-btn">
                  Cancel
                </Button>
              </div>
            </div>
          </Row>

          <Row>
            <Col xxl={12}>
              <Card>
                <CardBody>
                  <div className="live-preview">
                    <Row>
                      <Col md={6} xs={12}>
                        <div className="mb-3">
                          <Label htmlFor="productName" className="form-label">
                            상품명
                          </Label>
                          <Input type="text" className="form-control" placeholder="상품명" id="productName" value={name} onChange={(e) => setName(e.target.value)} maxLength={100}/>
                        </div>
                      </Col>
                      <Col md={6} xs={12}>
                        <div className="mb-3">
                          <Label htmlFor="ForminputState" className="form-label">
                            상태
                          </Label>
                            <select value={state} onChange={(e) => {
                    const nextState = e.target.value;
                    setState(nextState);
                  }} id="ForminputState" className="form-select" data-choices data-choices-sorting="true">
                            <option key="1" value="노출중">
                              노출중
                            </option>
                            <option key="2" value="어메이징패스">
                              어메이징패스
                            </option>
                            <option key="3" value="숨김">
                              숨김
                            </option>
                            <option key="4" value="품절">
                              품절
                            </option>
                          </select>
                        </div>
                      </Col>
                      <Col md={6} xs={12}>
                        <div className="mb-3">
                          <Label htmlFor="bundle" className="form-label">
                            티켓수
                          </Label>
                          <Input type="number" className="form-control" placeholder="티켓수" id="bundle" value={isLinkedPrecouponTicket ? 1 : bundle} onChange={(e) => setBundle(Number(e.target.value) < 0 || !e.target.value ? "" : e.target.value)} min="0" disabled={isLinkedPrecouponTicket}/>
                          {isLinkedPrecouponTicket ? (<p className="text-muted mt-2 mb-0" style={{ fontSize: "12px" }}>이 티켓은 예약권 판매설정에 연결되어 있어 티켓수는 1개로 고정됩니다. 예약권 발급 수량과 취소 방식은 예약권 상품의 판매설정에서 조정하세요.</p>) : null}
                        </div>
                      </Col>
                      <Col md={6} xs={12}>
                        <div className="mb-3">
                          <Label htmlFor="price" className="form-label">
                            가 격
                          </Label>
                          <Input type="number" className="form-control" placeholder="가격" id="price" value={price} onChange={(e) => setPrice(Number(e.target.value) < 0 || !e.target.value ? "" : e.target.value)} min="0"/>
                        </div>
                      </Col>
                      <Col md={6} xs={12}>
                        <div className="mb-3">
                          <Label htmlFor="membership-price" className="form-label">
                            유료회원가
                          </Label>
                          <Input type="number" className="form-control" placeholder="가격" id="membership-price" value={membershipPrice} onChange={(e) => setMembershipPrice(Number(e.target.value) < 0 || !e.target.value ? "" : e.target.value)} min="0"/>
                        </div>
                      </Col>
                      <Col md={6} xs={12}>
                        <div className="mb-3">
                          <Label htmlFor="canRefundEach" className="form-label">
                            묶음 티켓 개별 취소
                          </Label>
                          <div className="form-check form-switch">
                            <Input className="form-check-input" type="checkbox" id="canRefundEach" checked={canRefundEach} disabled={Number(isLinkedPrecouponTicket ? 1 : bundle || 1) <= 1} onChange={(e) => setCanRefundEach(e.target.checked)}/>
                            <Label className="form-check-label" htmlFor="canRefundEach">
                              허용
                            </Label>
                          </div>
                          <p className="text-muted mt-2 mb-0" style={{ fontSize: "12px" }}>{Number(bundle || 1) > 1 ? "켜면 1장씩 환불되고, 끄면 묶음 전체가 미사용일 때만 전체 환불됩니다." : "티켓수가 2개 이상인 묶음 상품에만 적용됩니다."}</p>
                        </div>
                      </Col>
                      <Col md={6} xs={12}>
                        <div className="mb-3">
                          <Label htmlFor="stock" className="form-label">
                            재 고
                            <Input style={{ marginLeft: 15 }} className="form-check-input" type="checkbox" id="formCheck6" checked={noLimit} onChange={(e) => {
            setStock(0);
            setNoLimit(e.target.checked);
        }}/>
                            <Label className="form-check-label" htmlFor="formCheck6">
                              제한없음
                            </Label>
                          </Label>
                          <Input type="number" className="form-control" placeholder="재고" id="stock" value={stock} onChange={(e) => {
            setStock(Number(e.target.value) < 0 || !e.target.value ? "" : e.target.value);
        }} min="0" disabled={noLimit}/>
                        </div>
                      </Col>
                      <Col md={6} xs={12}>
                        <div className="mb-3">
                          <Label htmlFor="thumbnail" className="form-label">
                            대표이미지
                          </Label>
                          <Input className="form-control" type="file" id="thumbnail" accept="image/jpg, image/jpeg, image/png" onChange={(e) => handleImageFiles(e, setThumbnail)}/>
                          {loadedImg ? (<div className="mt-2">
                              <a href={loadedImg} target="_blank" rel="noreferrer">
                                <img width="80px" src={loadedImg} alt={`${name} ticket thumbnail`}/>
                              </a>
                            </div>) : (<span style={{ color: "red" }}>* 필수</span>)}
                        </div>
                      </Col>
                      <Col md={6} xs={12}>
                        <div className="mb-3">
                          <Label htmlFor="detailImg" className="form-label">
                            상세이미지
                          </Label>
                          <Input className="form-control" type="file" id="detailImg" accept="image/jpg, image/jpeg, image/png" onChange={(e) => handleImageFiles(e, setDetailImg)} multiple/>
                        </div>
                      </Col>
                      <Col md={6} xs={12} style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                        <div className="mb-3">
                          <Row>
                            <Col md={3} xs={6}>
                              <Label htmlFor="ForminputState" className="form-label">
                                유효기간
                              </Label>
                              <select value={unit} onChange={(e) => setUnit(e.target.value as TicketExpirationUnit)} id="ForminputState" className="form-select" data-choices data-choices-sorting="true">
                                <option key="years" value="years">
                                  연
                                </option>
                                <option key="months" value="months">
                                  월
                                </option>
                                <option key="target_date" value="target_date">
                                  지정일
                                </option>
                              </select>
                            </Col>
                            <Col md={9} xs={6}>
                              <Label htmlFor="period" className="form-label" style={{ color: "#00ff0000" }}>
                                유효기간
                              </Label>
                              {unit !== "target_date" ? (<Input type="number" className="form-control" placeholder="유효기간" id="period" value={period} onChange={(e) => setPeriod(Number(e.target.value) < 0 || !e.target.value ? 0 : e.target.value)} min="0"/>) : (<CommonDatePicker value={target_date} onChange={(date) => {
                markDirty();
                setTargetDate(moment(date[0]).format("YYYY-MM-DD"));
            }} className="form-control" options={{
                parseDate: (datestr, format) => {
                    return moment(datestr).toDate();
                },
                formatDate: (date, format) => {
                    return moment(date).format("YYYY-MM-DD");
                },
            }}/>)}
                            </Col>
                            {unit !== "target_date" ? (
                              <Col xs={12}>
                                <p className="mt-2 mb-0 text-muted" style={{ fontSize: "12px", whiteSpace: "normal" }}>
                                  연/월 입력은 티켓 발급일부터 계산되는 상대 기간입니다. 예: 1년 = 티켓 발급 후 1년 뒤 만료
                                </p>
                              </Col>
                            ) : null}
                          </Row>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xxl={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">상세정보</h4>
                </CardHeader>
                <CardBody>
                  <div id="descriptEditor" tabIndex={-1} className="snow-editor" style={{ height: 300 }}>
                    <MyEditor value={descript} onChange={(value) => {
                      markDirty();
                      setDescript(value);
                    }} uid={1}/>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xxl={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">이용안내</h4>
                </CardHeader>
                <CardBody>
                  <div id="usingInfoEditor" tabIndex={-1} className="snow-editor" style={{ height: 300 }}>
                    <MyEditor value={usingInfo} onChange={(value) => {
                      markDirty();
                      setUsingInfo(value);
                    }} uid={2}/>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>);
};
export default TicketProductForm;
